import * as React from 'react'

import type {Quote} from '@/lib/food/foodModel'
import useGetStoreDetail from '@/lib/food/hooks/useGetStoreDetail'
import { useDeliveryType } from '@/lib/food/store'
import {StoreDetailRoute} from '@/routeRegistry'
import useParam from '@/utils/router/useParam'

import FoodStoreUI from './FoodStoreUI'

const defaultQuote: Quote = {
  time_estimate: {
    minimum: 0,
    maximum: 0,
    scheduled: [],
  },
  sales_tax_percent: 0,
  order_minimum: 0,
  delivery_fee: {
    delivery_fee_flat: 0,
    delivery_fee_percent: 0,
    delivery_fee_taxable: false,
  },
  service_fee: {
    service_fee_flat: 0,
    service_fee_percent: 0,
    service_fee_min: 0,
    service_fee_max: 0,
    service_fee_taxable: false,
  },
  small_order_fee: {
    minimum_order_value: 0,
    small_order_fee_flat: 0,
    small_order_fee_percent: 0,
  },
  asap_available: false,
  first_party_quote: false,
  third_party_quote: false,
  courier_quote: false,
  accepts_delivery_tip: false,
  accepts_pickup_tip: false,
  accepts_pickup_note: false,
}

export default memo(function FoodStore() {
  const [storeId] = useParam(StoreDetailRoute.id, 'storeId')
  const [deliveryType, setDeliveryType] = useState(false)
  const [_storageDeliveryType, setStorageDeliveryType] = useDeliveryType()

  useEffect(()=>{
    if(storeId.pickup_enabled && !storeId.delivery_enabled){
      setStorageDeliveryType("pickup")
    } else if(!storeId.pickup_enabled && storeId.delivery_enabled){
      setStorageDeliveryType("delivery")
    } else{
      setStorageDeliveryType(deliveryType ? "pickup" : "delivery");
    }
  },[deliveryType,storeId,setStorageDeliveryType])

  const query = useMemo(
    () => ({
      store_id: storeId.id,
      pickup: deliveryType,
      include_quote: true,
      quote_preference: 'first_available',
      include_customizations: true,
      user_latitude: storeId.lat,
      user_longitude: storeId.lng,
      user_street_num: storeId.user_street_num,
      user_street_name: storeId.user_street_name,
      user_city: storeId.user_city,
      user_state: storeId.user_state,
      user_zipcode: storeId.user_zipcode,
      user_country: storeId.user_country,
      available: true,
      include_fees: false,
      include_menu: true,
    }),
    [
      storeId.id,
      deliveryType,
      storeId.lat,
      storeId.lng,
      storeId.user_street_num,
      storeId.user_street_name,
      storeId.user_city,
      storeId.user_state,
      storeId.user_zipcode,
      storeId.user_country,
    ],
  )

  const {
    data: storeDetailData,
    isLoading: isStoreDetailLoading,
    isError: isStoreDetailError,
  } = useGetStoreDetail(query)

  const handleChangeDeliveryType = (value: boolean) => {
    setDeliveryType(value)
  }

  return (
    <FoodStoreUI
      isLoading={isStoreDetailLoading}
      isError={isStoreDetailError}
      storeId={storeId}
      store={storeDetailData?.categories ?? []}
      quote={storeDetailData?.quote ?? defaultQuote}
      deliveryType={deliveryType}
      changeDeliverytype={handleChangeDeliveryType}
    />
  )
})
